import { createContext, useContext } from 'react';
import type { IsomerSummary } from '@peruselab/api-client/isomers';
import type { ReactionZone } from '@peruselab/structure-editor/domain/reactionGraph';
import type { PaperPlacement } from '../../dnd/payloads';

/**
 * #689/#700 -- what the result cards (and My drawings) can put on the paper, provided by the
 * workspace shell (usePaperAdditions). Each resolves once added; `addMolecule` instead resolves
 * with the molecule's stereoisomers when there are several to choose from (then called again
 * with the chosen `isomerId`).
 */
export interface PaperAdditions {
  readonly addMolecule: (
    moleculeId: string,
    isomerId?: string,
    placement?: PaperPlacement
  ) => Promise<readonly IsomerSummary[] | null>;
  readonly addDrawing: (drawingId: string, placement?: PaperPlacement) => Promise<void>;
  readonly addIllustration: (illustrationId: string, placement?: PaperPlacement) => Promise<void>;
  /** #744 -- a saved reaction onto the paper (in place of the paper's own reaction, if any). */
  readonly addReaction: (reactionId: string) => Promise<void>;
  /** The reaction boxes, labelled, while the drawing has a reaction arrow: "Add to paper" then
   *  asks which box (it has no free spot beside the drawing -- that is where the arrow is). */
  readonly boxes: readonly { readonly zone: ReactionZone; readonly label: string }[] | null;
}

export const PaperAdditionsContext = createContext<PaperAdditions | null>(null);

/** Null outside the workspace (nothing can be added to a paper there). */
export function usePaperAdditions(): PaperAdditions | null {
  return useContext(PaperAdditionsContext);
}
